import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { findSecretNameProblem } from "./secret-name.ts";

/** GitHub's rule for an organization login: letters, digits and hyphens, not
 * starting with a hyphen, at most 39 characters. The connector refuses any
 * other org. */
export const GITHUB_ORG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,38}$/;

export const INVALID_ORG_LOGIN =
  "Enter the organization's GitHub login, as in github.com/<login>";

/** A GitHub App's ID, the number on the app's settings page. */
export const GITHUB_APP_ID_PATTERN = /^\d+$/;

export const INVALID_APP_ID =
  "The GitHub App ID is a number. Find it on the app's settings page.";

/** Where the GitHub App's private key is vaulted: AWS Secrets Manager or
 * Google Secret Manager. The connector runs in the vault's cloud, but where in
 * it is `hosting`'s to say. */
export const RepositoryAccessVault = withoutAzureKeyVault(
  newVaultElement({ owner: "integration" })
);

/** Where the connector runs: AWS Lambda or Google Cloud Run, in the vault's
 * cloud. */
export const RepositoryAccessHosting = ConnectorHosting;

export const GithubComponents = InstallSpec({
  // One item per GitHub organization, keyed by the org's login. Each org has
  // its own GitHub App and its own connector deployment.
  //
  // The backend checks the login (`GITHUB_ORG_PATTERN`) rather than an item
  // validator here: the install framework runs that validator first, and its
  // error repeats the whole request, which may hold a key pasted in place of
  // its secret's name.
  "repository-access": ItemComponent({
    label: "Organization",
    description:
      "Allow P0 to grant GitHub repository roles, team memberships and force-merge access through a connector you host",
    type: "string",
    maturity: "beta",
    // Where the key lives, the key's secret and where the connector runs are
    // fixed when the item is created: the connector's Terraform grants it read
    // on exactly that secret. A rotated key goes into the same secret, as a new
    // version. The app can change in place, for a new app, and the next install
    // check validates it.
    schema: {
      appId: Element({
        label: "GitHub App ID",
        description: "ID of the GitHub App you created for P0",
        type: "string",
        validator: async (_id, appId) =>
          GITHUB_APP_ID_PATTERN.test(appId.trim()) ? undefined : INVALID_APP_ID,
      }),
      vault: RepositoryAccessVault,
      // P0 points the connector at the secret by its partial ARN, in the
      // vault's account and region (`toSecretPartialArn`), or by its resource
      // name, in the vault's project (`toSecretResourceName`).
      privateKeySecretName: Element({
        label: "Private key secret",
        description:
          "The name of the secret that holds the GitHub App's private key: in the vault's account and region on AWS, or in its project on Google Cloud. P0 never reads it. Only your connector does.",
        type: "string",
        step: "new",
        // Can't tell which vault the name is for, so accepts a name either
        // vault could take. Installing checks it against the item's vault.
        validator: async (_id, name) => findSecretNameProblem(name),
      }),
      hosting: RepositoryAccessHosting,
    },
  }),
});
